import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, Loader2, Gauge } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { BookPlayer } from "@/components/player/BookPlayer";
import { makeTracker } from "@/lib/tracking";
import { LogoIcon, DevCredit } from "@/components/landing/Logo";
import { LoaderScreen } from "@/components/BrandLoader";
import { OfflineButton } from "@/components/player/OfflineButton";

const PasswordGate = ({ onSubmit, error }) => {
  const [pw, setPw] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(pw); }} className="w-80 neu-panel p-7" data-testid="password-gate">
      <Lock className="h-5 w-5 text-[#3D3DF0]" />
      <h2 className="mt-3 font-display text-2xl font-extrabold">Protected book</h2>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="mt-4 w-full rounded-xl border-0 neu-inset px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D3DF0]/40" data-testid="password-input" />
      {error && <p className="mt-2 text-xs text-[#A62B8A]">Incorrect password</p>}
      <button className="mt-4 w-full rounded-full neu-btn-primary py-2.5 text-sm font-medium" data-testid="password-submit">Open book</button>
    </form>
  );
};

const PerfBadge = ({ m }) => (
  <div className="flex items-center gap-3 font-mono text-[11px] text-[#64748B]" data-testid="perf-badge">
    <Gauge className="h-3.5 w-3.5 text-[#5FA82E]" />
    {m.first_page && <span>First page <span className="text-[#0F172A]">{(m.first_page / 1000).toFixed(2)}s</span></span>}
    {m.transition && <span>Turn <span className="text-[#0F172A]">{Math.round(m.transition)}ms</span></span>}
  </div>
);

export default function Reader({ mode }) {
  const { slug, bookId } = useParams();
  const [manifest, setManifest] = useState(null);
  const [state, setState] = useState("loading");
  const [pw, setPw] = useState(null);
  const [metrics, setMetrics] = useState({});

  const headers = pw ? { "X-Book-Password": pw } : {};
  const load = useCallback(async (password) => {
    try {
      const url = mode === "preview" ? `/books/${bookId}/manifest` : `/public/books/${slug}`;
      const { data } = await api.get(url, { headers: password ? { "X-Book-Password": password } : {} });
      setManifest(data);
      setState("ready");
      document.title = `${data.title} · iBookStudio`;
    } catch (e) {
      const s = e?.response?.status;
      setState(s === 401 && mode !== "preview" ? (password ? "badpw" : "password") : errMsg(e));
    }
  }, [mode, slug, bookId]);

  useEffect(() => { load(); }, [load]);

  const fetchText = () => api.get(mode === "preview" ? `/books/${bookId}/text` : `/public/books/${slug}/text`, { headers }).then((r) => r.data);
  const base = mode === "preview" ? `/books/${bookId}` : `/public/books/${slug}`;
  const fetchLines = useCallback(() => api.get(`${base}/lines`, { headers: pw ? { "X-Book-Password": pw } : {} }).then((r) => r.data), [base, pw]);
  const onQuiz = useCallback((body) => api.post(`${base}/quiz-attempts`, body, { headers: pw ? { "X-Book-Password": pw } : {} }).then((r) => r.data), [base, pw]);
  const onEvent = useMemo(() => (mode === "preview" ? undefined : makeTracker(slug, pw ? { "X-Book-Password": pw } : {})), [mode, slug, pw]);
  const onMetric = useCallback((k, v) => setMetrics((m) => (k === "first_page" && m.first_page ? m : { ...m, [k]: v })), []);

  if (state === "loading") return <LoaderScreen label="Opening your book" testid="reader-loading" />;
  if (state === "password" || state === "badpw") return <div className="grid h-screen place-items-center bg-[#EEF2F8]"><PasswordGate error={state === "badpw"} onSubmit={(p) => { setPw(p); load(p); }} /></div>;
  if (state !== "ready") return <div className="grid h-screen place-items-center bg-[#EEF2F8] text-sm text-[#64748B]" data-testid="reader-error">{state}</div>;

  return (
    <div className="flex h-[100dvh] flex-col bg-[#EEF2F8]" data-testid={`reader-${mode}`}>
      {mode !== "embed" && (
        <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-[#DCE3EE] px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/" className="shrink-0" aria-label="iBookStudio" data-testid="reader-brand-link"><LogoIcon className="h-7" /></Link>
            <h1 className="truncate font-display text-base font-bold" data-testid="reader-title">{manifest.title}</h1>
            <span className="shrink-0 rounded bg-[#E2E8F3] px-1.5 py-0.5 font-mono text-[10px] text-[#64748B]" data-testid="reader-version">{mode === "preview" ? "PREVIEW · DRAFT" : `v${manifest.version}`}</span>
          </div>
          {mode === "read" && <OfflineButton slug={slug} manifest={manifest} />}
          {mode === "preview" && <PerfBadge m={metrics} />}
        </header>
      )}
      <div className="min-h-0 flex-1">
        <BookPlayer manifest={manifest} variant={mode} fetchText={fetchText} fetchLines={fetchLines} onQuiz={onQuiz} onEvent={onEvent} onMetric={onMetric} />
      </div>
      {mode !== "embed" && <DevCredit className="shrink-0 py-1" />}
    </div>
  );
}
